import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Badge, Button, Form, Spinner } from "react-bootstrap";
import moment from "moment";
import {
	createLeagueComment,
	deleteLeagueComment,
	getLeagueComments,
} from "../../../services/leagueCommentService";
import { useAppSelector } from "../../../store/hooks";

const POLL_INTERVAL_MS = 10000;

const getRecordId = (record) => String(record?._id || record || "");

const LeagueComments = ({ leagueId }) => {
	const { user, isLoggedIn } = useAppSelector((state) => state.user);
	const { selectedLeague } = useAppSelector((state) => state.fantasy);
	const [comments, setComments] = useState([]);
	const [newComment, setNewComment] = useState("");
	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [deletingCommentId, setDeletingCommentId] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const chatFeedRef = useRef(null);

	const userId = user?._id || localStorage.getItem("userId") || "";
	const isCurrentLeague = getRecordId(selectedLeague) === String(leagueId || "");
	const isLeagueAdmin = selectedLeague?.admins?.some(
		(adminId) => getRecordId(adminId) === String(userId),
	);
	const isSiteAdmin = user?.role === "admin";
	const canModerateComments = Boolean(
		isLoggedIn && (isSiteAdmin || (isCurrentLeague && isLeagueAdmin)),
	);

	const fetchComments = useCallback(
		async (silent = true) => {
			if (!leagueId) {
				return;
			}

			if (!silent) {
				setIsLoading(true);
			}

			try {
				const res = await getLeagueComments(leagueId);
				const sortedComments = Array.isArray(res.data)
					? [...res.data].sort(
							(a, b) => new Date(a.datePosted) - new Date(b.datePosted),
						)
					: [];
				setComments(sortedComments);
			} catch (error) {
				console.error("Error fetching league comments:", error);
				setErrorMessage("Could not load chat right now. Please try again.");
			} finally {
				if (!silent) {
					setIsLoading(false);
				}
			}
		},
		[leagueId],
	);

	useEffect(() => {
		let isMounted = true;

		const loadComments = async () => {
			setErrorMessage("");
			await fetchComments(false);
		};

		loadComments();

		const intervalId = setInterval(() => {
			if (isMounted) {
				void fetchComments(true);
			}
		}, POLL_INTERVAL_MS);

		return () => {
			isMounted = false;
			clearInterval(intervalId);
		};
	}, [fetchComments]);

	useEffect(() => {
		if (chatFeedRef.current) {
			chatFeedRef.current.scrollTop = chatFeedRef.current.scrollHeight;
		}
	}, [comments]);

	const handleCommentSubmit = async () => {
		const content = newComment.trim();
		if (!content || !leagueId || !isLoggedIn) {
			return;
		}

		try {
			setIsSubmitting(true);
			setErrorMessage("");
			await createLeagueComment({
				content,
				league: leagueId,
			});
			setNewComment("");
			await fetchComments(true);
		} catch (error) {
			console.error("Error creating league comment:", error);
			setErrorMessage("Could not send message. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleComposerKeyDown = (event) => {
		if (event.key === "Enter" && !event.shiftKey) {
			event.preventDefault();
			void handleCommentSubmit();
		}
	};

	const handleDeleteComment = async (commentId) => {
		if (!commentId || !window.confirm("Delete this chat message?")) {
			return;
		}

		try {
			setDeletingCommentId(commentId);
			setErrorMessage("");
			await deleteLeagueComment(commentId);
			setComments((currentComments) =>
				currentComments.filter((comment) => comment._id !== commentId),
			);
		} catch (error) {
			console.error("Error deleting league comment:", error);
			setErrorMessage("Could not delete message. Please try again.");
		} finally {
			setDeletingCommentId("");
		}
	};

	return (
		<div className="league-live-chat">
			<div className="d-flex justify-content-between align-items-center mb-3">
				<div className="text-white fw-bold fs-4">LIVE CHAT</div>
				<Badge bg="secondary" text="light" pill>
					Updates every 10s
				</Badge>
			</div>

			<div className="league-live-chat__feed mb-3" ref={chatFeedRef}>
				{isLoading ? (
					<div className="h-100 d-flex align-items-center justify-content-center">
						<Spinner animation="border" variant="light" size="sm" />
						<span className="text-light ms-2">Loading chat...</span>
					</div>
				) : comments.length === 0 ? (
					<div className="text-center text-muted py-4">
						No messages yet. Start the conversation.
					</div>
				) : (
					comments.map((comment) => {
						const authorId = String(comment?.author?._id || comment?.author || "");
						const isOwnComment = String(userId) === authorId;
						return (
							<div
								key={comment._id}
								className={`league-live-chat__message ${
									isOwnComment ? "league-live-chat__message--self" : ""
								}`}
							>
								<div className="league-live-chat__meta">
									<span>{comment?.author?.username || "Rider"}</span>
									<span>{moment(comment.datePosted).fromNow()}</span>
									{canModerateComments ? (
										<Button
											variant="link"
											className="p-0 text-danger text-decoration-none small"
											onClick={() => handleDeleteComment(comment._id)}
											disabled={deletingCommentId === comment._id}
										>
											{deletingCommentId === comment._id
												? "Deleting..."
												: "Delete"}
										</Button>
									) : null}
								</div>
								<div className="league-live-chat__bubble">{comment.content}</div>
							</div>
						);
					})
				)}
			</div>

			<Form.Group controlId="newComment" className="text-start text-white mt-auto">
				<Form.Control
					as="textarea"
					rows={3}
					className="league-live-chat__composer"
					value={newComment}
					placeholder={
						isLoggedIn
							? "Type a message..."
							: "Sign in to join this league chat."
					}
					onKeyDown={handleComposerKeyDown}
					onChange={(event) => setNewComment(event.target.value)}
					disabled={!isLoggedIn || isSubmitting}
				/>
			</Form.Group>
			<div className="d-flex justify-content-between align-items-center mt-2">
				<div className="text-muted small">
					{isLoggedIn
						? "Press Enter to send, Shift+Enter for a new line."
						: "You can still read chat messages while signed out."}
				</div>
				<Button
					variant="dark"
					className="fw-semibold"
					onClick={handleCommentSubmit}
					disabled={!isLoggedIn || !newComment.trim() || isSubmitting}
				>
					{isSubmitting ? "Sending..." : "Send"}
				</Button>
			</div>
			{errorMessage ? (
				<Alert variant="danger" className="mt-3 mb-0 py-2">
					{errorMessage}
				</Alert>
			) : null}
		</div>
	);
};

export default LeagueComments;
